import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';

import * as d3 from 'd3';

import ReactTooltip from 'react-tooltip';

const styles = (theme) => ({
  container: {
    position: 'absolute',
    top: '20px',
    left: '20px',
  },
  imageContainer: {
    border: '1px solid black',
    [theme.breakpoints.up('tablet')]: {
      width: '120px',
      height: '80px',
      clipPath: 'ellipse(50px 35px at 50% 45%)',
    },
    [theme.breakpoints.up('md')]: {
      width: '120px',
      height: '80px',
      clipPath: 'ellipse(50px 35px at 50% 45%)',
    },
    [theme.breakpoints.up('lg')]: {
      left: '5px',
      top: '10px',
      width: '160px',
      height: '140px',
      clipPath: 'ellipse(70px 44px at 50% 32%)',
    },
    [theme.breakpoints.up('xl')]: {
      left: '10px',
      top: '40px',
      width: '180px',
      height: '190px',
      clipPath: 'ellipse(70px 45px at 50% 25%)',
    },
  },
  image: {
    display: 'block',
    margin: '0 auto ',
    //border:'1px solid red',
    backgroundImage: 'url("/images/offshore_wind_map_white.svg")',
    backgroundSize: 'contain!important',
    backgroundRepeat: 'no-repeat!important',
    backgroundPosition: 'center center!important',
    width: '60%',
    height: 0,

    [theme.breakpoints.down('xs')]: {
      maxWidth: '45px',
      height: '45px',
    },
    [theme.breakpoints.up('sm')]: {
      maxWidth: '70px',
      height: '70px',
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: '80px',
      height: '80px',
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: '100px',
      maxHeight: '60px',
      marginTop: '10px',
    },
  },
  centered: {
    position: 'relative',
    top: '50%',
    transform: 'translateY(-50%)',
    marginLeft: '-5px',
    fontFamily: 'Flanders Art Sans!important',
    fontWeight: '600',
  },
  leftTableColumn: {
    //width: '75%',
    width: '230px',
    textAlign: 'left',
    fontFamily: 'Flanders Art Sans!important',
  },
  rightTableColumn: {
    //width: '25%',
    textAlign: 'right',
    fontFamily: 'Flanders Art Sans!important',
  },
  toolTip: {
    opacity: '0.7!important',
    fontFamily: 'Flanders Art Sans!important',
    //minWidth:'280px',
    color: 'black!important',
  },
});

function MapOffshoreIcon(props) {
  const { classes } = props;

  //const unit= 'Wh';
  //const format = d3.format('~s');

  const [currentState, setCurrentState] = useState({
    valueScale: d3.scaleLinear().clamp(true),
    colorScale: d3.scaleLinear().interpolate(d3.interpolateRgb),
    unitHour: 'Wh',
    unitMonth: 'Wh',
    componentUpdated: false,
    data: [],
    currentTech: {
      solar: true,
      wind: false,
      windOffshore: false,
    },
    currentFilter: 'energy',
    currentMode: 'realtime',
    hasForecast: false,
  });

  useEffect(() => {
    if (props.dataReady & (props.data !== undefined) && props.data.length > 0) {
      let stateUpdated =
        JSON.stringify(props.currentTech) ===
          JSON.stringify(currentState.currentTech) &&
        props.currentFilter === currentState.currentFilter &&
        props.currentMode === currentState.currentMode &&
        props.currentDateTime === currentState.currentDateTime &&
        props.data === currentState.data;
      if (!stateUpdated || !currentState.componentUpdated) updateIconData();
    }
  });

  if (!props.show) return <div />;

  function updateIconData() {
    let valueScale = currentState.valueScale;
    let colorScale = currentState.colorScale;

    let unitHour = currentState.unitHour;
    let unitMonth = currentState.unitMonth;

    // map js color function
    valueScale.range([0, props.colorArray.length - 1]);
    colorScale.range(
      props.colorArray.map(function (hex) {
        return d3.color(hex);
      }),
    );

    //const maxValue=d3.max(props.nisSums, function (t) { return t.values })

    colorScale.domain(
      [...Array(props.colorArray.length).keys()].map(valueScale.invert),
    );
    valueScale.domain([0, props.maxValue]).nice();
    colorScale.domain(
      [...Array(colorScale.domain().length).keys()].map(valueScale.invert),
    );

    let inw;
    switch (props.lang) {
      case 'de':
        inw = 'einw';
        break;
      case 'en':
        inw = 'inh';
        break;
      case 'fr':
      default:
        inw = 'hab';
    }

    switch (props.currentFilter) {
      case 'inhabitants':
        unitHour = 'Wh/' + inw;
        unitMonth = 'kWh/' + inw;
        break;
      case 'surface':
        unitHour = 'MWh/km²';
        unitMonth = 'GWh/km²';
        break;
      default:
      case 'energy':
        unitHour = 'MWh';
        unitMonth = 'GWh';
    }

    let currentTech = props.currentTech;
    let currentFilter = props.currentFilter;
    let currentMode = props.currentMode;
    //let currentDateTime = props.currentDateTime
    /*
    let currentData = props.data.filter(function (v) {
      return (v.datetime.getTime() === new Date(currentDateTime).getTime())
    })[0].values
    */
    //let backgroundColor=currentState.colorScale(currentData);

    const actualDate = new Date();
    const currentSelectedDate = new Date(props.currentDateTime);
    const hasForecast =
      currentSelectedDate.toDateString() === actualDate.toDateString() ||
      currentSelectedDate.getTime() > actualDate.getTime();

    setCurrentState({
      colorScale: colorScale,
      valueScale: valueScale,
      componentUpdated: true,
      currentTech: currentTech,
      currentFilter: currentFilter,
      currentMode: currentMode,
      data: props.data,
      unitHour: unitHour,
      unitMonth: unitMonth,
      currentDateTime: props.currentDateTime,
      hasForecast: hasForecast,
    });
  }

  let currentData;
  if (
    props.dataReady &&
    currentState.data !== undefined &&
    currentState.data.length > 0
  ) {
    let currentDateTime = currentState.currentDateTime;
    currentData = currentState.data.filter(function (v) {
      return v.datetime.getTime() === new Date(currentDateTime).getTime();
    })[0].values;
  }

  const currentMode = props.currentMode;

  const currentYear = parseInt(d3.timeFormat('%Y')(new Date()));
  const energyUsePerFamily =
    props.currentMode === 'realtime'
      ? 3500 / 365.25
      : props.yearSelected === currentYear
      ? props.data !== undefined && props.data.length > 0
        ? (3500 * props.data.length) / 12
        : 0
      : 3500;

  const hasForecast = currentState.hasForecast;
  const unitHour = currentState.unitHour;
  const unitMonth = currentState.unitMonth;

  let municipalityLabel;
  //let inhabitantAbbreviation
  let familiesProvidedLabel;
  let powerLabel;
  let productionLastHourLabel;
  let productionLastMonthLabel;
  let dayProductionLabel;
  let expectedDayProductionLabel;
  let monthProductionLabel;
  let expectedMonthProductionLabel;
  //let co2AvoidedLabel

  switch (props.lang) {
    case 'de':
      municipalityLabel = 'Gemeente';
      productionLastHourLabel = 'Productie afgelopen uur (in ';
      productionLastMonthLabel = 'Productie afgelopen maand (in ';
      //inhabitantAbbreviation = "inw"
      dayProductionLabel = 'Productie volledige dag (in ';
      expectedDayProductionLabel = 'Verwachte productie volledige dag (in';
      monthProductionLabel = 'Productie volledig jaar (in ';
      expectedMonthProductionLabel = 'Verwachte productie volledig jaar (in';
      familiesProvidedLabel = 'Gezinnen voorzien';
      powerLabel = 'Vermogen (in MW)';
      //co2AvoidedLabel = "CO\u2082 avoided (ton)"
      break;
    case 'en':
      municipalityLabel = 'Township';
      productionLastHourLabel = 'Production last hour (in ';
      productionLastMonthLabel = 'Production last month (in ';
      //inhabitantAbbreviation = "inh"
      dayProductionLabel = 'Production whole day (in ';
      expectedDayProductionLabel = 'Expected production whole day (in';
      monthProductionLabel = 'Production whole month (in ';
      expectedMonthProductionLabel = 'Expected production whole month (in';
      familiesProvidedLabel = 'Families provided';
      powerLabel = 'Power (in MW)';
      //co2AvoidedLabel = "CO\u2082 avoided (ton)"
      break;
    case 'fr':
      municipalityLabel = 'Commune';
      productionLastHourLabel = 'Production dernière heure (en ';
      productionLastMonthLabel = 'Production dernier mois (en ';
      //inhabitantAbbreviation = "hab"
      dayProductionLabel = 'Production journée complète (en ';
      expectedDayProductionLabel = 'Production prévue toute la journée (en ';
      monthProductionLabel = 'Production mois complet (en ';
      expectedMonthProductionLabel = 'Production prévue tout le mois (en ';
      familiesProvidedLabel = 'Familles fournies';
      powerLabel = 'Puissance installée (en MW)';
      //co2AvoidedLabel = "CO\u2082 évité (ton)"
      break;
    case 'nl':
    default:
      municipalityLabel = 'Gemeente';
      productionLastHourLabel = 'Productie afgelopen uur (in ';
      productionLastMonthLabel = 'Productie afgelopen maand (in ';
      //inhabitantAbbreviation = "inw"
      dayProductionLabel = 'Productie volledige dag (in ';
      expectedDayProductionLabel = 'Verwachte productie volledige dag (in ';
      monthProductionLabel = 'Productie volledig jaar (in ';
      expectedMonthProductionLabel = 'Verwachte productie volledig jaar (in ';
      familiesProvidedLabel = 'Gezinnen voorzien';
      powerLabel = 'Vermogen (in MW)';
    //co2AvoidedLabel = "CO\u2082 évité (ton)"
  }

  let reactToolTip;
  const localeOption =
    props.lang === 'de' ? 'de-DE' : props.lang === 'en' ? 'en-GB' : 'fr-BE';
  //const co2AvoidedFactor = props.co2AvoidedFactor
  if (currentData !== undefined) {
    const municipalityName = 'Offshore windturbines';
    const productionLastHour = currentData;
    reactToolTip = (
      <ReactTooltip id="offshore" className={classes.toolTip + ' bgmediumgrey'}>
        <table style={{ minWidth: '300px' }}>
          <tbody>
            <tr>
              <td className={classes.leftTableColumn}>{municipalityLabel}</td>
              <td className={classes.rightTableColumn}>
                <b>{municipalityName}</b>
              </td>
            </tr>
            <tr>
              <td className={classes.leftTableColumn}>
                {currentMode === 'realtime'
                  ? productionLastHourLabel + unitHour + ')'
                  : productionLastMonthLabel + unitMonth + ')'}
              </td>
              <td className={classes.rightTableColumn}>
                <b>
                  {isNaN(productionLastHour)
                    ? '0'
                    : currentMode === 'realtime'
                    ? props.currentFilter === 'inhabitants'
                      ? (productionLastHour * 10).toLocaleString(localeOption, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }) // factor 1000 for going from kWh to Wh
                      : Math.round(productionLastHour).toLocaleString(
                          localeOption,
                        )
                    : props.currentFilter === 'inhabitants'
                    ? (Math.floor(productionLastHour / 10) / 10).toLocaleString(
                        localeOption,
                      )
                    : (
                        Math.floor(productionLastHour / 100) / 10
                      ).toLocaleString(localeOption)}
                </b>
              </td>
            </tr>
            <tr>
              <td className={classes.leftTableColumn}>
                {currentMode === 'realtime'
                  ? hasForecast
                    ? expectedDayProductionLabel + unitHour + ')'
                    : dayProductionLabel + unitHour + ')'
                  : hasForecast
                  ? expectedMonthProductionLabel + unitMonth + ')'
                  : monthProductionLabel + unitMonth + ')'}
              </td>
              <td className={classes.rightTableColumn}>
                <b>
                  {isNaN(props.dayProduction)
                    ? '0'
                    : currentMode === 'realtime'
                    ? props.currentFilter === 'inhabitants'
                      ? (props.dayProduction * 10).toLocaleString(
                          localeOption,
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        ) // factor 1000 for going from kWh to Wh
                      : Math.round(props.dayProduction).toLocaleString(
                          localeOption,
                        )
                    : props.currentFilter === 'inhabitants'
                    ? (
                        Math.floor(props.dayProduction / 10) / 10
                      ).toLocaleString(localeOption)
                    : (
                        Math.floor(props.dayProduction / 100) / 10
                      ).toLocaleString(localeOption)}
                </b>
              </td>
            </tr>
            <tr>
              <td className={classes.leftTableColumn}>
                {familiesProvidedLabel}
              </td>
              <td className={classes.rightTableColumn}>
                <b>
                  {isNaN(props.dayProduction)
                    ? '0'
                    : Math.floor(
                        (props.dayProduction * 1000) / energyUsePerFamily,
                      ).toLocaleString('nl-BE')}
                </b>
              </td>
            </tr>
            <tr>
              <td className={classes.leftTableColumn}>{powerLabel}</td>
              <td className={classes.rightTableColumn}>
                <b>
                  {isNaN(props.capacity)
                    ? '0'
                    : (Math.round(10 * props.capacity) / 10).toLocaleString(
                        localeOption,
                      )}
                </b>
              </td>
            </tr>
            {/*
                <tr>
                  <td className={classes.leftTableColumn}>{co2AvoidedLabel}</td>
                  <td className={classes.rightTableColumn}><b>{
                    isNaN(props.dayProduction) ?
                      "0" :
                      (currentMode === 'realtime' ?
                        (props.currentFilter === 'inhabitants' ?
                          (co2AvoidedFactor * props.dayProduction * 10 / 1000).toLocaleString(localeOption, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) // factor 1000 for going from kWh to Wh
                          : Math.round(co2AvoidedFactor * props.dayProduction / 1000).toLocaleString(localeOption)
                        )
                        :
                        (props.currentFilter === 'inhabitants' ?
                          (Math.floor(co2AvoidedFactor * props.dayProduction / 10/1000) / 10).toLocaleString(localeOption) :
                          (Math.floor(co2AvoidedFactor * props.dayProduction / 100) / 10 / 1000).toLocaleString(localeOption)
                        )
                      )
                  }</b></td>
                </tr>
              */}
          </tbody>
        </table>
      </ReactTooltip>
    );
  }
  let backgroundColor = props.currentTech.windOffshore
    ? currentState.colorScale(currentData)
    : '#dbdbdb';

  //let offshoreWindEnergy
  /*
  switch (props.lang) {
    case "de":
      offshoreWindEnergy = "Wind vor der Küste in der Nordsee"
      break;
    case "en":
      offshoreWindEnergy = "Wind offshore in North Sea"
      break;
    case "fr":
        offshoreWindEnergy = "Éolien offshore en mer du nord"
        break
        case 'nl':
    default:
        offshoreWindEnergy = "Offshore windturbines in de Noordzee"
  }
  */
  return (
    <div className={classes.container}>
      {/*
        <div className={classes.textContainer}>{offshoreWindEnergy}</div>
        */}
      <div
        className={classes.imageContainer}
        style={{ backgroundColor: backgroundColor }}
      >
        <div
          className={classes.image}
          data-tip="offshore"
          data-for="offshore"
        ></div>
      </div>
      {reactToolTip}
    </div>
  );
}

MapOffshoreIcon.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(MapOffshoreIcon);
